"use client"

import { useState } from "react"
import { motion, useReducedMotion } from "framer-motion"
import {
  Check,
  ChevronLeft,
  Loader2,
  MessageCircle,
  Search,
  SquarePen,
  Trash2,
} from "lucide-react"

import { RichTextEditor } from "@/components/rich-text-editor"
import { OverlayNav, OverlayNavAction } from "@/components/graphite/overlay-nav"
import { ShowMore } from "@/components/graphite/show-more"
import { SideRail, RAIL_LABEL } from "@/components/graphite/side-rail"
import { TextAction } from "@/components/graphite/text-action"
import { RobinAvatar } from "@/components/agentic-ats/robin/robin-avatar"
import { COMMENT_LABEL_OPTIONS, type CommentLabel } from "@/lib/comment-labels"
import { useVisibleBatch } from "@/hooks/use-visible-batch"
import { cn } from "@/lib/utils"
import type { CommentDetail, CommentScope, CommentSummary } from "@/hooks/use-candidate-comments"
import { RichText } from "@/components/graphite/rich-text"

/**
 * Comments on the candidate record, from canvas artboards 14a/14o (view) and
 * 14b/14f (composer), with 14h/14r and 14i/14m as their light counterparts.
 *
 * The canvas is emphatic about the shape: "Rebuilt flat: the overlay is the
 * sheet body, divided by hairlines, not a card floating inside a card." So
 * nothing here sits on a surface of its own — these render straight into the
 * record body, and the record closes its tab selection behind them, because a
 * comment is a full document rather than a sidebar item.
 *
 * Purely presentational. Everything comes in as props from
 * `useCandidateComments`.
 */


/** Author and anything else the contract actually carries about a comment. */
function Provenance({ rows }: { rows: { label: string; value: string; mono?: boolean }[] }) {
  if (rows.length === 0) return null
  return (
    <div className="flex-none">
      {rows.map((row) => (
        <div
          key={row.label}
          className="flex items-baseline gap-4 border-t py-[9px]"
        >
          <span className="w-[88px] flex-none text-[11px] leading-[1.4] text-muted-foreground">
            {row.label}
          </span>
          <span
            className={cn(
              "min-w-0 flex-1 overflow-hidden text-[12px] leading-[1.4]",
              row.mono && "font-mono text-[11.5px]",
            )}
          >
            {row.value}
          </span>
        </div>
      ))}
    </div>
  )
}

function AgentNote({ note }: { note: string }) {
  return (
    <div className="mt-5 flex flex-none items-start gap-[9px] border-t pt-4">
      <RobinAvatar state="idle" className="mt-px h-4 w-4 flex-none" />
      <span className="flex-1 text-[12px] leading-[1.6] text-muted-foreground">
        {note}
      </span>
    </div>
  )
}

/** The prose itself. Bodies are stored as rich text, so they are sanitized. */
function CommentBody({ html, large }: { html: string; large?: boolean }) {
  return <RichText html={html} size={large ? "md" : "sm"} />
}


/**
 * One comment as a row: label, when, two lines of what it says, who wrote it.
 *
 * The rail draws these beside an open comment and the list draws the same rows
 * full width, so they are one component — the only difference is that a row in
 * the rail can be the one currently open.
 */
function CommentRow({
  comment,
  isSelected,
  onSelect,
  layoutId,
}: {
  comment: CommentSummary
  isSelected?: boolean
  onSelect: () => void
  /**
   * Ties this row to the comment it opens, so the two ends of the trip are one
   * object and the row grows into the document rather than being replaced by it.
   * Only the list passes one — the rail draws the same rows beside an open
   * comment, and two elements claiming the same id is not a shared transition,
   * it is a fight.
   */
  layoutId?: string
}) {
  return (
    <motion.button
      layoutId={layoutId}
      type="button"
      onClick={onSelect}
      className={cn(
        "w-full border-t py-3 text-left transition-colors",
        isSelected
          ? "pl-[11px] shadow-[inset_2px_0_0_var(--primary)]"
          : "hover:opacity-80",
      )}
    >
      <span className="flex items-baseline gap-2.5">
        <span
          className={cn(
            "text-[11px] font-medium",
            isSelected ? "text-foreground" : "text-muted-foreground",
          )}
        >
          {comment.label}
        </span>
        <span className="ml-auto font-mono text-[10px] text-muted-foreground">
          {comment.when}
        </span>
      </span>
      <span className="mt-1.5 line-clamp-2 block text-[11.5px] leading-[1.55] text-muted-foreground">
        {comment.preview}
      </span>
      {comment.author && (
        <span className="mt-[7px] block font-mono text-[10px] text-muted-foreground">
          {comment.author}
        </span>
      )}
    </motion.button>
  )
}

/**
 * Narrowing the list: a text search over what a comment says, what kind it is
 * and who wrote it, and 14a's "Job only / All" scope beside it.
 *
 * The artboards draw the scope and not the search. The pre-Graphite viewer had
 * the search and people use it, so it stays — the artboard is a picture of a
 * screen, not a list of what the screen may do.
 */
function CommentFilters({
  search,
  onSearchChange,
  scope,
  onScopeChange,
  canScopeToJob,
}: {
  search: string
  onSearchChange: (search: string) => void
  scope: CommentScope
  onScopeChange: (scope: CommentScope) => void
  canScopeToJob: boolean
}) {
  const [searching, setSearching] = useState(false)

  return (
    <>
      <div className="mt-3 flex flex-none items-baseline">
        {searching || search ? (
          <input
            autoFocus
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            onBlur={() => setSearching(false)}
            placeholder="Search comments"
            aria-label="Search comments"
            className="w-full border-b bg-transparent pb-1 text-[11px] outline-none transition-colors focus:border-primary"
          />
        ) : (
          <button
            type="button"
            onClick={() => setSearching(true)}
            className="inline-flex items-center gap-[7px] text-[11px] text-muted-foreground transition-colors hover:text-foreground"
          >
            <Search className="h-[13px] w-[13px]" />
            Search comments
          </button>
        )}
      </div>

      {canScopeToJob && (
        <div className="mt-3.5 flex flex-none items-center gap-3.5">
          {(["job", "all"] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => onScopeChange(option)}
              className={cn(
                "border-b pb-[3px] text-[11px] transition-colors",
                scope === option
                  ? "border-primary font-medium text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {option === "job" ? "Job only" : "All"}
            </button>
          ))}
        </div>
      )}
    </>
  )
}

/** The related-comments rail — 14o folds it into a count where it cannot fit. */
function RelatedRail({
  related,
  count,
  selectedId,
  scope,
  onScopeChange,
  canScopeToJob,
  onSelect,
  search,
  onSearchChange,
}: {
  related: CommentSummary[]
  count: number
  selectedId?: string
  scope: CommentScope
  onScopeChange: (scope: CommentScope) => void
  canScopeToJob: boolean
  onSelect: (id: string) => void
  search: string
  onSearchChange: (search: string) => void
}) {
  /*
    The open comment is kept inside the window whatever the window's size — it
    is the row this rail exists to sit beside, and reading the ninetieth comment
    should not be the one case where it is missing from the list of them.
  */
  const { visible, remaining, showMore } = useVisibleBatch(
    related,
    `${scope}:${search}`,
    selectedId ? related.findIndex((comment) => comment.id === selectedId) : undefined,
  )

  return (
    <SideRail
      label="Comments"
      count={
        search && related.length !== count ? `${related.length}/${count}` : count
      }
      header={
        <CommentFilters
          search={search}
          onSearchChange={onSearchChange}
          scope={scope}
          onScopeChange={onScopeChange}
          canScopeToJob={canScopeToJob}
        />
      }
    >
      {search && related.length === 0 ? (
        <p className="border-t py-3 text-[11.5px] text-muted-foreground">
          No comment mentions “{search}”.
        </p>
      ) : null}
      {visible.map((comment) => (
        <CommentRow
          key={comment.id}
          comment={comment}
          isSelected={comment.id === selectedId}
          onSelect={() => onSelect(comment.id)}
        />
      ))}
      <ShowMore remaining={remaining} onShowMore={showMore} noun="comments" />
    </SideRail>
  )
}

/**
 * Every comment on the record, which is what the Comments count now opens.
 *
 * Turn 20 is explicit about the rail being a launchpad: "Counts are the trigger.
 * A row with a number opens the list; the action beside it opens the composer."
 * The count used to open the composer instead, so there was no way to simply
 * read what had already been written — you were made to write.
 *
 * The rail is not drawn beside this, because the rail is this list; at every
 * width the body carries it, and picking a row opens 14a with the rail back.
 */
export function CommentList({
  related,
  count,
  total,
  search,
  onSearchChange,
  scope,
  onScopeChange,
  canScopeToJob,
  onSelect,
  onNew,
  onBack,
  isCounted = true,
  isError = false,
}: {
  related: CommentSummary[]
  count?: number
  /** Before the search narrowed them, so the count can say both. */
  total: number
  search: string
  onSearchChange: (search: string) => void
  scope: CommentScope
  onScopeChange: (scope: CommentScope) => void
  canScopeToJob: boolean
  onSelect: (id: string) => void
  onNew: () => void
  onBack: () => void
  /** False while `/comments` is unresolved — the list then says so. */
  isCounted?: boolean
  /**
   * Whether that request *failed*. `isCounted` is false for a pending read and
   * a failed one alike, and telling someone their comments are still loading
   * when nothing is loading is the same untruth as telling them there are none.
   */
  isError?: boolean
}) {
  // Nobody who has asked for less movement wants a card flying across the panel.
  const zoom = !useReducedMotion()

  /*
    A record worked for a year can carry hundreds of these, and every preview is
    parsed out of stored rich text. Searching or changing scope re-windows it.
  */
  const { visible, remaining, showMore } = useVisibleBatch(
    related,
    `${scope}:${search}`,
  )

  return (
    <div className="flex h-full min-h-0 flex-col px-4 py-4 @2xl:px-[26px] @2xl:py-5">
      {/*
        Back sits at every width, not only on narrow: the record hides its header
        during a takeover below 768px, and a 1440 window with Robin open leaves
        the record 765.
      */}
      <OverlayNav
        onBack={onBack}
        backLabel="Back to the record"
        title="Comments"
        meta={
          <span className="font-mono text-[11px] text-muted-foreground">
            {search && related.length !== total
              ? `${related.length}/${total}`
              : (count ?? "—")}
          </span>
        }
        actions={
          <OverlayNavAction tone="teal" onClick={onNew}>
            New
          </OverlayNavAction>
        }
      />

      <div className="flex-none">
        <CommentFilters
          search={search}
          onSearchChange={onSearchChange}
          scope={scope}
          onScopeChange={onScopeChange}
          canScopeToJob={canScopeToJob}
        />
      </div>

      <div className="mt-3 min-h-0 flex-1 overflow-y-auto">
        {isError ? (
          <p className="border-t py-3 text-[12px] text-muted-foreground">
            The comments could not be read right now.
          </p>
        ) : !isCounted ? (
          <p className="border-t py-3 text-[12px] text-muted-foreground">
            Reading the comments…
          </p>
        ) : related.length === 0 ? (
          <p className="border-t py-3 text-[12px] text-muted-foreground">
            {search
              ? `No comment mentions “${search}”.`
              : "Nothing has been written on this record yet."}
          </p>
        ) : (
          <>
            {visible.map((comment) => (
              <CommentRow
                key={comment.id}
                comment={comment}
                layoutId={zoom ? `comment-${comment.id}` : undefined}
                onSelect={() => onSelect(comment.id)}
              />
            ))}
            <ShowMore
              remaining={remaining}
              onShowMore={showMore}
              noun="comments"
            />
          </>
        )}
      </div>
    </div>
  )
}

export function CommentView({
  comment,
  related,
  count,
  scope,
  onScopeChange,
  canScopeToJob,
  onSelect,
  onEdit,
  onDelete,
  onBack,
  onOpenList,
  isBusy,
  search,
  onSearchChange,
}: {
  comment: CommentDetail
  related: CommentSummary[]
  count: number
  search: string
  onSearchChange: (search: string) => void
  scope: CommentScope
  onScopeChange: (scope: CommentScope) => void
  canScopeToJob: boolean
  onSelect: (id: string) => void
  onEdit: () => void
  onDelete: () => void
  /**
   * One step out to the record, from the wide action row — where the rail is
   * already showing the other comments and the list would be a step sideways.
   */
  onBack: () => void
  /**
   * Back to the rest of them, which is what "back" means wherever the rail
   * cannot fit. 14o: "read one, tap 14 to get back to the rest."
   */
  onOpenList: () => void
  isBusy: boolean
}) {
  const zoom = !useReducedMotion()

  /*
    14a reads provenance as a spec sheet. Every row is skipped when its fact is
    not there: a candidate-scoped comment has no job. 14a also draws the stage a
    comment was written at; nothing records it (the endpoint that was read for
    it queried a table that exists nowhere), so the row is gone rather than
    fed by a request that always failed.
  */
  const provenance = [
    ...(comment.author ? [{ label: "Author", value: comment.author }] : []),
    ...(comment.job ? [{ label: "Job", value: comment.job }] : []),
  ]

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden px-4 py-4 @2xl:px-[26px] @2xl:py-5">
        {/*
          Narrow gets a nav bar, per 14o: the record header is not on screen.

          Back goes to the list here, not to the record. Where the rail cannot
          fit, the list is the only place the other comments exist, and stepping
          straight past it to the profile skips the thing a reader who was just
          browsing comments is going back *to*. On wide the rail is beside them
          already, so the wide Back below is the one that leaves for the record.
        */}
        <OverlayNav
          className="@xl:hidden"
          size="comfortable"
          onBack={onOpenList}
          backLabel="Back to all comments"
          title="Comment"
          actions={
            <OverlayNavAction size="comfortable" tone="teal" onClick={onEdit}>
              Edit
            </OverlayNavAction>
          }
        />

        {/*
          The head of the document carries the list row's own id, so opening a
          comment is the row growing into it rather than one screen swapping for
          another. The body has nothing to grow from, so it rises into place
          behind — a shorter move, and it starts once the card has landed.
        */}
        <motion.div
          layoutId={zoom ? `comment-${comment.id}` : undefined}
          data-comment-head
          className="flex-none"
        >
          <div className="flex items-baseline gap-3.5">
            <span className="text-[12px] font-medium text-brand-ink">
              {comment.label}
            </span>
            <span className="ml-auto font-mono text-[11px] text-muted-foreground @xl:ml-0">
              {comment.writtenAt}
            </span>
          </div>

          <div className="mt-3.5">
            <Provenance rows={provenance} />
          </div>
        </motion.div>

        <motion.div
          initial={zoom ? { opacity: 0, y: 8 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.22, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
          className="mt-5 min-h-0 flex-1 overflow-y-auto border-t pt-[18px]"
        >
          <CommentBody html={comment.body} />
        </motion.div>

        {comment.agentNote && <AgentNote note={comment.agentNote} />}

        {/* Wide keeps Edit and Delete under the body; narrow has Edit in the
            nav bar and the count bar below.

            Back leads, because a comment takes the whole body and the artboard
            draws no way out of it on wide — the tab strip above is the only one,
            and a reader looking at a document does not read a dimmed tab as an
            exit. One step, to the tab they were on, exactly as 14o gives narrow. */}
        <div className="mt-4 hidden flex-none gap-[22px] border-t pt-3.5 @xl:flex">
          <TextAction onClick={onBack}>
            <ChevronLeft className="h-3.5 w-3.5" />
            Back
          </TextAction>
          <TextAction tone="teal" onClick={onEdit} disabled={isBusy}>
            <SquarePen className="h-3.5 w-3.5" />
            Edit
          </TextAction>
          <TextAction onClick={onDelete} disabled={isBusy}>
            {isBusy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
            Delete
          </TextAction>
        </div>

        <div className="-mx-4 mt-4 flex flex-none items-center gap-3 border-t bg-chrome px-4 @xl:hidden">
          <TextAction onClick={onDelete} disabled={isBusy} className="h-[52px]">
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </TextAction>
          {/* 14o: "The rail cannot come along, so the related-comments list
              collapses to a count in the bottom bar: read one, tap 14 to get
              back to the rest." */}
          <button
            type="button"
            onClick={onOpenList}
            aria-label={`All ${count} comments`}
            className="ml-auto inline-flex h-[52px] items-center gap-[7px] text-[12.5px] text-muted-foreground"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            <span className="font-mono">{count}</span>
          </button>
        </div>
      </div>

      <RelatedRail
        related={related}
        count={count}
        selectedId={comment.id}
        scope={scope}
        onScopeChange={onScopeChange}
        canScopeToJob={canScopeToJob}
        onSelect={onSelect}
        search={search}
        onSearchChange={onSearchChange}
      />
    </div>
  )
}

/**
 * The composer, from 14b: the ten stored labels laid out as one field of text
 * selected by a teal underline — "no dropdown, no boxes, nothing hidden" — with
 * the toolbar under the editor and quiet text actions at the foot.
 */
export function CommentComposer({
  initialLabel = "note",
  initialBody = "",
  isEditing,
  onSubmit,
  onCancel,
  isBusy,
}: {
  initialLabel?: CommentLabel
  initialBody?: string
  isEditing?: boolean
  onSubmit: (label: CommentLabel, body: string) => void
  onCancel: () => void
  isBusy: boolean
}) {
  const [label, setLabel] = useState<CommentLabel>(initialLabel)
  const [body, setBody] = useState(initialBody)

  return (
    <div className="flex h-full min-h-0 flex-col px-4 pt-4 @2xl:px-[26px] @2xl:pt-5">
      <div className="flex-none">
        <div className={RAIL_LABEL}>Comment type</div>
        <div className="mt-3 flex flex-wrap gap-x-[22px] border-b pb-4">
          {COMMENT_LABEL_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setLabel(option.value)}
              className={cn(
                "border-b py-1.5 text-[12px] transition-colors",
                label === option.value
                  ? "border-primary font-medium text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/*
        The shared editor, which already draws 14b's shape: the field flush with
        the page and the toolbar under it on a hairline.

        This used to be a bare `contentEditable` seeded with
        `dangerouslySetInnerHTML` and driven by `document.execCommand`. React
        owns the children of a node it renders `dangerouslySetInnerHTML` into,
        so every keystroke that updated state rewrote the node underneath the
        caret — which is why the field could not be typed into at all.
      */}
      <RichTextEditor
        className="flex-1 pt-[18px]"
        content={initialBody}
        onChange={setBody}
        placeholder="What happened, and what it means for this candidate?"
        trailing={
          <span className="font-mono text-[10px] text-muted-foreground">
            saves to Timeline
          </span>
        }
      />

      <div className="flex flex-none items-center gap-[22px] border-t pb-5 pt-4">
        <TextAction
          tone="teal"
          onClick={() => onSubmit(label, body)}
          disabled={isBusy || !body.trim()}
          className="text-[12.5px]"
        >
          {isBusy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="h-3.5 w-3.5" />
          )}
          {isEditing ? "Save changes" : "Post comment"}
        </TextAction>
        <TextAction onClick={onCancel} disabled={isBusy} className="text-[12.5px]">
          Cancel
        </TextAction>
      </div>
    </div>
  )
}
